import { useState, useRef, useEffect } from "react";
import { Tag, ChevronDown, CheckCircle2, AlertCircle, X, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { formatMoney } from "../utils/format";
import "./CouponBox.css";

/**
 * Accessible, CRO-optimized promo code accordion component.
 * Implements anti-churn collapsible styling, instant feedback, and real-time recalculation.
 *
 * @param {Object} props
 * @param {number} [props.customCartTotal] - Optional cart total override (e.g. for checkout specific totals)
 * @param {string} [props.className] - Optional custom CSS container class
 */
export default function CouponBox({ customCartTotal, className = "" }) {
  const { t, isRTL, language } = useLanguage();
  const {
    appliedCoupon,
    discountAmount,
    validatingCoupon,
    applyCoupon,
    removeCoupon,
  } = useCart();

  const [isOpen, setIsOpen] = useState(Boolean(appliedCoupon));
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);

  // If a coupon is applied, keep the view clean (showing the green badge)
  // If no coupon is applied, allow toggling the accordion
  useEffect(() => {
    if (appliedCoupon) {
      setErrorMessage("");
      setCode("");
    }
  }, [appliedCoupon]);

  const toggleAccordion = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    setErrorMessage("");
    if (nextOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanCode = code.trim();
    if (!cleanCode) {
      setErrorMessage(t("coupon.enterCode"));
      inputRef.current?.focus();
      return;
    }

    setErrorMessage("");
    const result = await applyCoupon(cleanCode, customCartTotal);
    if (!result.success) {
      setErrorMessage(result.message || "Invalid coupon code.");
      inputRef.current?.focus();
    } else {
      setCode("");
    }
  };

  const handleRemove = () => {
    removeCoupon();
    setErrorMessage("");
    setIsOpen(false);
  };

  // State A: Coupon is already applied -> Show high-trust applied badge
  if (appliedCoupon) {
    const savedAmountText = formatMoney(discountAmount, language);
    return (
      <div className={`coupon-box coupon-box--applied ${className}`} dir={isRTL ? "rtl" : "ltr"}>
        <div className="coupon-box__applied-card">
          <div className="coupon-box__applied-info">
            <span className="coupon-box__applied-icon" aria-hidden="true">
              <CheckCircle2 size={16} />
            </span>
            <div className="coupon-box__applied-text">
              <div className="coupon-box__applied-header">
                <span className="coupon-box__applied-code">{appliedCoupon.code}</span>
                <span className="coupon-box__applied-pill">
                  {t("coupon.saved", { amount: savedAmountText })}
                </span>
              </div>
              <p className="coupon-box__applied-note">
                {appliedCoupon.message || t("coupon.applied", { code: appliedCoupon.code })}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="coupon-box__remove-btn"
            onClick={handleRemove}
            title={t("coupon.remove")}
            aria-label={t("coupon.remove")}
          >
            <X size={15} />
            <span className="coupon-box__remove-text">{t("coupon.remove")}</span>
          </button>
        </div>
      </div>
    );
  }

  // State B: No coupon applied -> Subtle, non-intrusive collapsible accordion
  return (
    <div className={`coupon-box ${className}`} dir={isRTL ? "rtl" : "ltr"}>
      <button
        type="button"
        className={`coupon-box__trigger ${isOpen ? "coupon-box__trigger--open" : ""}`}
        onClick={toggleAccordion}
        aria-expanded={isOpen}
        aria-controls="coupon-accordion-panel"
      >
        <div className="coupon-box__trigger-label">
          <Tag size={15} className="coupon-box__tag-icon" aria-hidden="true" />
          <span>{t("coupon.havePromo")}</span>
        </div>
        <ChevronDown
          size={15}
          className={`coupon-box__chevron ${isOpen ? "coupon-box__chevron--open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div id="coupon-accordion-panel" className="coupon-box__panel" role="region">
          <form onSubmit={handleSubmit} className="coupon-box__form" noValidate>
            <div className="coupon-box__input-wrapper">
              <input
                ref={inputRef}
                type="text"
                className={`coupon-box__input ${errorMessage ? "coupon-box__input--error" : ""}`}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder={t("coupon.placeholder")}
                disabled={validatingCoupon}
                maxLength={50}
                autoComplete="off"
                spellCheck="false"
                aria-invalid={Boolean(errorMessage)}
                aria-describedby={errorMessage ? "coupon-error-msg" : undefined}
              />
              <button
                type="submit"
                className="coupon-box__apply-btn"
                disabled={validatingCoupon || !code.trim()}
              >
                {validatingCoupon ? (
                  <>
                    <Loader2 size={13} className="spinner" style={{ animation: "spin 1s linear infinite" }} />
                    <span>{t("coupon.applying")}</span>
                  </>
                ) : (
                  <span>{t("coupon.apply")}</span>
                )}
              </button>
            </div>

            {errorMessage && (
              <div id="coupon-error-msg" className="coupon-box__error" role="alert">
                <AlertCircle size={14} className="coupon-box__error-icon" aria-hidden="true" />
                <span>{errorMessage}</span>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
